import { httpClient } from "@/lib/httpClient";
import {
  SessionAssetsResponseSchema,
  SessionQuestionsResponseSchema,
  SessionResponseSchema,
  StreamHandshakeSchema,
  AudioPreviewSchema,
  ProjectSchema,
  ProjectScriptsResponseSchema,
  SessionOnboardingResponseSchema
} from "@/api/schemas";
import type {
  CustomQuestionPayload,
  SessionCreatePayload,
  SessionEventPayload,
  SessionQuestionUpdatePayload,
  StreamHandshakePayload,
  SessionOnboardingPayload,
  ProjectScript
} from "@/api/types";
import { parseWithSchema, toApiError } from "@/api/utils";
import { appEnv } from "@/config/env";

export async function createSession(payload: SessionCreatePayload) {
  try {
    const { data } = await httpClient.post("/interview/session", payload);
    return parseWithSchema(SessionResponseSchema, data);
  } catch (error) {
    throw toApiError(error);
  }
}

export async function getSession(sessionId: string) {
  try {
    const { data } = await httpClient.get(`/interview/session/${sessionId}`);
    return parseWithSchema(SessionResponseSchema, data);
  } catch (error) {
    throw toApiError(error);
  }
}

export async function listSessionQuestions(sessionId: string) {
  try {
    const { data } = await httpClient.get(`/interview/session/${sessionId}/questions`);
    const sessionParsed = SessionResponseSchema.safeParse(data);
    if (sessionParsed.success) {
      return sessionParsed.data.questions;
    }
    const questionsParsed = parseWithSchema(SessionQuestionsResponseSchema, data);
    return questionsParsed.questions;
  } catch (error) {
    throw toApiError(error);
  }
}

export async function updateSessionQuestion(
  sessionId: string,
  sessionQuestionId: string,
  payload: SessionQuestionUpdatePayload
) {
  try {
    await httpClient.patch(`/interview/session/${sessionId}/questions/${sessionQuestionId}`, payload);
  } catch (error) {
    throw toApiError(error);
  }
}

export async function createCustomQuestion(sessionId: string, payload: CustomQuestionPayload) {
  try {
    const { data } = await httpClient.post(
      `/interview/session/${sessionId}/questions/custom`,
      payload
    );
    const parsed = parseWithSchema(
      SessionResponseSchema.extend({
        audio_preview: AudioPreviewSchema
      }),
      data
    );
    return parsed;
  } catch (error) {
    throw toApiError(error);
  }
}

export async function listSessionAssets(sessionId: string, params?: Record<string, string>) {
  try {
    const { data } = await httpClient.get(`/interview/session/${sessionId}/assets`, {
      params
    });
    return parseWithSchema(SessionAssetsResponseSchema, data);
  } catch (error) {
    throw toApiError(error);
  }
}

export async function getSessionQuestionAudio(sessionId: string, sessionQuestionId: string) {
  try {
    const { data } = await httpClient.get<{ signed_url: string | null }>(
      `/interview/session/${sessionId}/questions/${sessionQuestionId}/audio`,
      {
        params: {
          signed_url_ttl_seconds: 3600
        }
      }
    );
    if (!data?.signed_url) {
      throw new Error("Audio asset is not ready yet.");
    }
    return data.signed_url;
  } catch (error) {
    throw toApiError(error);
  }
}

export async function postSessionEvent(sessionId: string, payload: SessionEventPayload) {
  try {
    await httpClient.post(`/interview/session/${sessionId}/events`, payload);
  } catch (error) {
    throw toApiError(error);
  }
}

export async function bootstrapStream(payload: StreamHandshakePayload) {
  try {
    const { data } = await httpClient.post("/interview/stream/bootstrap", payload);
    return parseWithSchema(StreamHandshakeSchema, data);
  } catch (error) {
    throw toApiError(error);
  }
}

export function buildStreamUrl(
  sessionId: string,
  query: {
    resume?: boolean;
    chat?: string;
    profile?: string;
    response?: string;
    transcribeOutput?: boolean;
  }
): string {
  const baseOrigin =
    appEnv.apiBaseUrl && appEnv.apiBaseUrl.startsWith("http")
      ? new URL(appEnv.apiBaseUrl)
      : new URL(
          appEnv.apiBaseUrl || "/api",
          typeof window !== "undefined" ? window.location.origin : "http://localhost:8000"
        );

  const wsUrl = new URL(baseOrigin.toString());
  wsUrl.protocol = baseOrigin.protocol === "https:" ? "wss:" : "ws:";
  wsUrl.pathname = `/interview/stream/${sessionId}`;
  const params = new URLSearchParams();
  if (typeof query.resume !== "undefined") {
    params.set("resume", String(query.resume));
  }
  if (query.chat) {
    params.set("chat", query.chat);
  }
  if (query.profile) {
    params.set("profile", query.profile);
  }
  if (query.response) {
    params.set("response", query.response);
  }
  if (typeof query.transcribeOutput !== "undefined") {
    params.set("transcribe_output", String(query.transcribeOutput));
  }
  wsUrl.search = params.toString();
  return wsUrl.toString();
}

export async function lookupProject(displayName: string) {
  try {
    const { data } = await httpClient.get(
      `/interview/projects/by-display-name/${encodeURIComponent(displayName)}`
    );
    return parseWithSchema(ProjectSchema, data);
  } catch (error) {
    throw toApiError(error);
  }
}

export async function listProjectScripts(projectId: string) {
  try {
    const { data } = await httpClient.get(`/interview/projects/${projectId}/scripts`);
    const parsed = parseWithSchema(ProjectScriptsResponseSchema, data);
    return parsed.scripts as ProjectScript[];
  } catch (error) {
    throw toApiError(error);
  }
}

export async function patchSessionOnboarding(sessionId: string, payload: SessionOnboardingPayload) {
  try {
    const { data } = await httpClient.patch(`/interview/session/${sessionId}/onboarding`, payload);
    return parseWithSchema(SessionOnboardingResponseSchema, data);
  } catch (error) {
    throw toApiError(error);
  }
}

